import React from 'react';
import RobbieMark from './RobbieMark';

/**
 * v2 badge — small count/label marker. `robbie` tone is for AI-attributed
 * content ONLY (one-job-per-color: violet = Robbie) and renders as the mock's
 * insight chip: the xxs mascot mark + label on the AI-chip gradient — the
 * mascot IS the attribution, never the letters "AI" (Design System §06).
 */
export type BadgeTone = 'default' | 'action' | 'robbie';

const tones: Record<Exclude<BadgeTone, 'robbie'>, string> = {
  default: 'bg-neutral-bg text-neutral-ink border-neutral-border',
  action: 'bg-action-bg text-action-deep border-action-border',
};

export interface BadgeProps {
  tone?: BadgeTone;
  mono?: boolean; // counts/IDs read better in mono
  /**
   * robbie tone only: the mascot mark (default true). Opt out ONLY where a
   * RobbieMark sits directly adjacent (e.g. the panel-header insight count).
   */
  mark?: boolean;
  /**
   * robbie tone only: chip geometry (28px tall, fully round) so it shares a
   * row with `ReadOnlyChip` — e.g. Robbie's readings beside saved criteria.
   */
  chip?: boolean;
  /** Hover text, e.g. where a Robbie chip's reading came from. */
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export const Badge: React.FC<BadgeProps> = ({ tone = 'default', mono, mark = true, chip, title, children, className = '' }) => {
  if (tone === 'robbie') {
    return (
      <span
        title={title}
        className={
          `inline-flex items-center gap-s2 border border-robbie-border bg-ai-chip px-s4 ` +
          `${chip ? 'h-7 rounded-round' : 'rounded-pill py-[3px]'} ` +
          `${mono ? 'font-mono text-mono-data' : 'text-body-sm font-medium'} text-robbie-ink ${className}`
        }
      >
        {mark && <RobbieMark size="xxs" />}
        {children}
      </span>
    );
  }
  return (
    <span
      title={title}
      className={
        `inline-flex min-w-[18px] items-center justify-center rounded-round border px-s2 py-[1px] ` +
        `${mono ? 'font-mono text-mono-data' : 'text-meta font-medium'} ${tones[tone]} ${className}`
      }
    >
      {children}
    </span>
  );
};

export default Badge;
